/**
 * B: Revenue quality - client concentration, movers, new vs repeat.
 * WiP ledger only.
 */
import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Legend,
  Tooltip as RechartsTooltip,
} from "recharts";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { useCfoData, TabState, KpiStrip, Card, SourceBadge, InfoTip } from "./cfoShared";
import CountrySplit from "./CountrySplit";
import CountryOverrides from "./CountryOverrides";

export default function ClientsTab() {
  const { data, loading, error } = useCfoData("cfo/quality");
  const [countryVer, setCountryVer] = useState(0);
  if (loading || error) return <TabState loading={loading} error={error} testid="cfo-clients" />;

  const latest = data.concentration[data.concentration.length - 1];
  const latestNR = data.new_vs_repeat[data.new_vs_repeat.length - 1];

  return (
    <div className="flex flex-col gap-4" data-testid="cfo-clients">
      <KpiStrip cards={[
        { label: `Top-5 client share · ${latest.year}`, value: `${latest.top5_share}%`, tone: latest.top5_share > 50 ? "text-rose-700" : "text-[#1B4F8A]", testid: "clients-kpi-top5", info: "cl_top5" },
        { label: `Top-10 client share · ${latest.year}`, value: `${latest.top10_share}%`, tone: "text-slate-900", testid: "clients-kpi-top10", info: "cl_top10" },
        { label: `Active clients · ${latest.year}`, value: latest.clients, tone: "text-teal-700", testid: "clients-kpi-active", info: "cl_active" },
        { label: `Repeat revenue share · ${latestNR.year}`, value: `${latestNR.repeat_share}%`, tone: "text-[#1B4F8A]", testid: "clients-kpi-repeat", info: "cl_repeat", sub: `${latestNR.new_clients} new clients this year` },
      ]} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="clients-concentration">
          <div className="flex items-center justify-between mb-1">
            <PanelTitle>Concentration by year <InfoTip id="cl_concentration" /></PanelTitle>
            <SourceBadge kind="wip" />
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.concentration} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={38} />
                <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, n) => [`${v}%`, n === "top5_share" ? "top 5 share" : "top 10 share"]} />
                <Legend wrapperStyle={{ fontSize: 10 }} formatter={(v) => (v === "top5_share" ? "top 5" : "top 10")} />
                <Bar dataKey="top5_share" fill="#1B4F8A" maxBarSize={28} radius={[2, 2, 0, 0]} />
                <Bar dataKey="top10_share" fill="#3EA8DC" fillOpacity={0.6} maxBarSize={28} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mt-3 mb-1">Top clients · {latest.year}</p>
          <ul className="space-y-1">
            {latest.top_clients.slice(0, 5).map((c) => (
              <li key={c.label} className="flex items-center justify-between text-xs">
                <span className="font-medium text-slate-800 truncate">{c.label}</span>
                <span className="font-mono tabular-nums text-stone-500">{fmtGbpShort(c.value)} · {c.share}%</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card testid="clients-new-repeat">
          <PanelTitle>New vs repeat revenue <InfoTip id="cl_new_repeat" /></PanelTitle>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.new_vs_repeat} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={54} />
                <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, n) => [fmtGbpShort(v), n === "repeat_value" ? "repeat clients" : "new clients"]} />
                <Legend wrapperStyle={{ fontSize: 10 }} formatter={(v) => (v === "repeat_value" ? "repeat" : "new")} />
                <Bar dataKey="repeat_value" stackId="nr" fill="#0D9488" maxBarSize={36} />
                <Bar dataKey="new_value" stackId="nr" fill="#F59E0B" maxBarSize={36} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-stone-400 mt-2">
            "New" = first ever billed that year (pre-2023 history is used to classify, not counted as revenue).
          </p>
        </Card>
      </div>

      <Card testid="clients-movers">
        <PanelTitle>Client movers · top 40 by revenue since Jan 23 <InfoTip id="cl_movers" /></PanelTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100">
                <th className="py-1.5 text-left font-medium">Client</th>
                {Object.keys(data.movers[0]?.years || {}).map((y) => (
                  <th key={y} className="py-1.5 text-right font-medium">{y}</th>
                ))}
                <th className="py-1.5 text-right font-medium">{data.cur_year} committed</th>
                <th className="py-1.5 text-right font-medium">Δ {data.movers[0]?.delta_years || "YoY"}</th>
                <th className="py-1.5 text-right font-medium">Last billed</th>
              </tr>
            </thead>
            <tbody>
              {data.movers.map((m) => (
                <tr key={m.label} data-testid={`clients-mover-${m.label}`} className="border-b border-stone-50 last:border-0">
                  <td className="py-1.5 font-medium text-slate-800 truncate max-w-[180px]">
                    {m.label}
                    {m.quiet && (
                      <span className="ml-1.5 text-[9px] font-mono uppercase px-1 py-0.5 rounded border text-stone-500 bg-stone-50 border-stone-200">quiet 6m+</span>
                    )}
                  </td>
                  {Object.entries(m.years).map(([y, v]) => (
                    <td key={y} className="py-1.5 text-right font-mono tabular-nums text-stone-600">
                      {v ? fmtGbpShort(v) : <span className="text-stone-300">-</span>}
                    </td>
                  ))}
                  <td
                    className="py-1.5 text-right font-mono tabular-nums text-slate-800"
                    data-testid={`clients-mover-committed-${m.label}`}
                  >
                    {m.committed_cur_year ? (
                      <>
                        {fmtGbpShort(m.committed_cur_year)}
                        {m.scheduled_rest > 0 && (
                          <span className="text-[9px] text-teal-700 ml-1">
                            +{fmtGbpShort(m.scheduled_rest)} due
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-stone-300">-</span>
                    )}
                  </td>
                  <td className={`py-1.5 text-right font-mono tabular-nums ${m.delta > 0 ? "text-teal-700" : m.delta < 0 ? "text-rose-700" : "text-stone-400"}`}>
                    {m.delta != null ? `${m.delta > 0 ? "+" : ""}${fmtGbpShort(m.delta)}` : "-"}
                  </td>
                  <td className="py-1.5 text-right font-mono tabular-nums text-stone-500">{m.last_billed || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <CountrySplit key={countryVer} />

      <CountryOverrides
        kind="customer"
        noun="client"
        onSaved={() => setCountryVer((v) => v + 1)}
        testid="country-overrides-customer"
      />
    </div>
  );
}
